import { useCallback, useState } from 'react';
import { Button, Text } from 'rizzui';
import { Loader } from '../../../components/loader/SpinnerLogo';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import Page from '../../../layout/Page/Page';
import { FacilitatorCallsService } from '../../../services/calls/facilitatorCallsService';

const cardClassName = 'rounded-md border border-gray-200 bg-white p-4';
const columnOrder = [
    'coordinatorName',
    'facilitatorName',
    'facilitatorUserEmail',
    'totalCalls',
    'totalStartCalls',
    'totalFollowUpCalls',
    'totalEndCalls',
    'totalRealizedCalls',
    'totalPendingCalls',
];

const FacilitatorAssignments = () => {
    const service = new FacilitatorCallsService();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters({}, [], 1, 25);

    const [response, loading, error, refetch] = useFetch(useCallback(async () => {
        const apiResponse = await service.listSystemFacilitators(filters);

        return apiResponse.getResponseData();
    }, [filters]));

    const rows = response?.data ?? [];
    const summary = response?.summary ?? {
        facilitatorsTotal: 0,
        assignedContracts: 0,
        totalCalls: 0,
        totalRealizedCalls: 0,
        totalPendingCalls: 0,
    };
    const lastPage = response?.lastPage ?? 1;

    const orderedData = () => rows.map((row: any) => ({
        facilitatorUserId: row.facilitatorUserId,
        coordinatorName: row.coordinatorName || 'N/A',
        facilitatorName: [row.facilitatorUserName, row.facilitatorUserLastName].filter(Boolean).join(' ') || 'Sin nombre',
        facilitatorUserEmail: row.facilitatorUserEmail || 'N/A',
        totalCalls: row.totalCalls || 0,
        totalStartCalls: row.totalStartCalls || 0,
        totalFollowUpCalls: row.totalFollowUpCalls || 0,
        totalEndCalls: row.totalEndCalls || 0,
        totalRealizedCalls: row.totalRealizedCalls || 0,
        totalPendingCalls: row.totalPendingCalls || 0,
    }));

    if (loading && !response) {
        return <Loader height="50vh" />;
    }

    if (!loading && error) {
        return (
            <Page container="fluid">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    No se ha podido cargar el listado de asignaciones de dinamizador.
                </div>
            </Page>
        );
    }

    return (
        <Page container="fluid">
            <div className="mb-6 grid gap-4 md:grid-cols-5">
                {[
                    { label: 'Dinamizadores', value: summary.facilitatorsTotal },
                    { label: 'Contratos', value: summary.assignedContracts },
                    { label: 'Llamadas', value: summary.totalCalls },
                    { label: 'Realizadas', value: summary.totalRealizedCalls },
                    { label: 'Pendientes', value: summary.totalPendingCalls },
                ].map((card) => (
                    <div key={card.label} className={cardClassName}>
                        <Text className="text-xs uppercase text-gray-500">{card.label}</Text>
                        <Text className="mt-2 text-2xl font-semibold text-gray-900">{card.value}</Text>
                    </div>
                ))}
            </div>

            <CustomTable
                id="facilitator-assignments-table"
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                columnsNotShown={[
                    'facilitatorUserId'
                ]}
                overrideColumns={[
                    {
                        key: 'facilitatorName',
                        render: (row: any) => <Text className="font-bold text-primary">{row.row.original.facilitatorName}</Text>,
                    },
                ]}
                filters={filters}
                updateFilters={updateFilters}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={filters.filter_order || undefined}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: lastPage,
                    totalCount: response?.totalRegisters || rows.length,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
                filter={false}
            >
                <Button variant="outline" onClick={() => refetch()}>
                    Actualizar
                </Button>
            </CustomTable>
        </Page>
    );
};

export default FacilitatorAssignments;
